"use client";

import { useState } from "react";
import { RotateCcw, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { ChoiceControl } from "../shared/ChoiceControl";
import { Locale, t } from "@/lib/localization";
import {
    KnowledgeCheckBlock as KnowledgeCheckBlockType,
    QuizAppearance,
    QuizOption,
    QuizQuestion,
    ResolvedQuizAppearance,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import { resolveQuizAppearance, quizType } from "../../../lib/blockRegistry";
import RichText from "../text/richText";
import RichHtml from "./richHtml";
import { seedTitleHtml } from "../../../lib/titleHtml";
import { QuizInstruction, QuizInstructionInput } from "./quizInstruction";
import {
    OpenFeedback,
    OpenFeedbackInput,
    OptionFeedback,
    OptionFeedbackInput,
    optionFeedback,
} from "./quizFeedback";
import { fieldTag } from "../../../lib/sectionRegistry";
import {
    openAnswerClass,
    optionCardClass,
    quizSurfaceStyle,
} from "./quizAnswerStyle";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

/** True/False answers aren't editable, so they always follow the lomda's language. */
const trueFalseLabel = (index: number, locale: Locale) =>
    t(
        index === 0
            ? "lomda-v3-editor.quiz.true"
            : "lomda-v3-editor.quiz.false",
        locale
    );

/** Strip tags to test whether rich text actually has content. */
const hasText = (html: string) =>
    !!html && html.replace(/<[^>]*>/g, "").trim().length > 0;

/** Editable question card (inline on canvas) — matches the Figma design. */
function QuestionCard({
    q,
    index,
    appearance,
    blockId,
    gridGap,
    onPatch,
    onPatchOption,
    onToggleCorrect,
    onFocus,
}: {
    q: QuizQuestion;
    index: number;
    appearance: ResolvedQuizAppearance;
    blockId: string;
    gridGap: number;
    onPatch: (patch: Partial<QuizQuestion>) => void;
    onPatchOption: (optId: string, patch: Partial<QuizOption>) => void;
    onToggleCorrect: (optId: string) => void;
    onFocus: () => void;
}) {
    const questionTag = fieldTag("knowledge-check", "question");
    const lomdaLocale = useLomdaLocale();
    const type = quizType(q);
    const multiple = type === "multiple";

    return (
        <div
            onFocusCapture={onFocus}
            onPointerDownCapture={onFocus}
            className="flex flex-col gap-4"
        >
            {/* Question text — rich text: focusing it shows the text toolbar. */}
            <div>
                <RichText
                    initialHtml={seedTitleHtml(q.text, questionTag)}
                    defaultTag={questionTag}
                    placeholder={t("lomda-v3-editor.quiz.question-placeholder")}
                    editable
                    useGlobalToolbar
                    blockId={blockId}
                    groupKey={`${blockId}:kc-question`}
                    onCommit={(text) => onPatch({ text })}
                />
                <QuizInstructionInput
                    question={q}
                    onChange={(instruction) => onPatch({ instruction })}
                />
            </div>

            {/* Answer area */}
            {type === "open" ? (
                <textarea
                    disabled
                    placeholder={t("lomda-v3-editor.quiz.open-answer-placeholder")}
                    style={{
                        backgroundColor: appearance.background,
                        borderColor: appearance.border,
                        borderWidth: appearance.borderWidth,
                    }}
                    className="lomda-v3-card-text min-h-[120px] w-full resize-none rounded-xl border p-4 lomda-v3-text-p outline-none placeholder:text-[hsl(var(--lomda-muted-text))]"
                />
            ) : (
                <div
                    className="grid grid-cols-1 sm:grid-cols-2"
                    style={{ gap: gridGap }}
                >
                    {q.options.map((o, oi) => (
                        <div
                            key={o.id}
                            style={{
                                backgroundColor: appearance.background,
                                borderColor: appearance.border,
                                borderWidth: appearance.borderWidth,
                            }}
                            className="lomda-v3-card-text flex min-h-14 flex-wrap content-start items-center gap-4 rounded-xl border px-5 py-3"
                        >
                            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--lomda-muted))] lomda-v3-text-small text-[hsl(var(--lomda-primary))]">
                                {LETTERS[oi] ?? oi + 1}
                            </span>
                            {type === "true-false" ? (
                                <span className="min-w-0 flex-1 lomda-v3-text-p">
                                    {trueFalseLabel(oi, lomdaLocale)}
                                </span>
                            ) : (
                                <div className="min-w-0 flex-1">
                                    <RichText
                                        initialHtml={o.text}
                                        placeholder={t(
                                            "lomda-v3-editor.quiz.option-placeholder"
                                        )}
                                        editable
                                        useGlobalToolbar
                                        blockId={blockId}
                                        groupKey={`${blockId}:kc-option`}
                                        onCommit={(text) =>
                                            onPatchOption(o.id, { text })
                                        }
                                    />
                                </div>
                            )}
                            <button
                                type="button"
                                title={t("lomda-v3-editor.quiz.mark-correct")}
                                onClick={() => onToggleCorrect(o.id)}
                                className="shrink-0"
                            >
                                <ChoiceControl
                                    variant={multiple ? "checkbox" : "radio"}
                                    status={o.correct ? "on" : "off"}
                                />
                            </button>
                            <OptionFeedbackInput
                                option={o}
                                letter={LETTERS[oi] ?? String(oi + 1)}
                                onChange={(feedback) =>
                                    onPatchOption(o.id, { feedback })
                                }
                            />
                        </div>
                    ))}
                </div>
            )}
            {type === "open" && (
                <OpenFeedbackInput
                    question={q}
                    onChange={(explanation) => onPatch({ explanation })}
                />
            )}
        </div>
    );
}

function PreviewQuiz({
    questions,
    gridGap,
    appearance,
}: {
    questions: QuizQuestion[];
    gridGap: number;
    /** As stored: an unset field keeps the theme class. */
    appearance: QuizAppearance | undefined;
}) {
    const locale = useLomdaLocale();
    const surfaceStyle = quizSurfaceStyle(appearance);
    const questionTag = fieldTag("knowledge-check", "question");
    const [answers, setAnswers] = useState<Record<string, string[]>>({});
    const [openAnswers, setOpenAnswers] = useState<Record<string, string>>({});
    const [checked, setChecked] = useState(false);

    const toggle = (q: QuizQuestion, optId: string) => {
        setAnswers((a) => {
            const cur = a[q.id] ?? [];
            if (q.multiple) {
                return {
                    ...a,
                    [q.id]: cur.includes(optId)
                        ? cur.filter((x) => x !== optId)
                        : [...cur, optId],
                };
            }
            return { ...a, [q.id]: [optId] };
        });
    };

    const isCorrect = (q: QuizQuestion) => {
        const sel = new Set(answers[q.id] ?? []);
        const correct = q.options.filter((o) => o.correct).map((o) => o.id);
        return (
            sel.size === correct.length && correct.every((id) => sel.has(id))
        );
    };

    const isOpen = (q: QuizQuestion) => quizType(q) === "open";
    const gradable = questions.filter((q) => !isOpen(q));
    const answered = questions.every((q) =>
        isOpen(q)
            ? (openAnswers[q.id] ?? "").trim().length > 0
            : (answers[q.id]?.length ?? 0) > 0
    );
    const score = gradable.filter(isCorrect).length;

    return (
        <div className="flex flex-col gap-10">
            {questions.map((q, qi) => {
                const type = quizType(q);
                const open = type === "open";
                const picked = answers[q.id] ?? [];
                const showsFeedback = (o: QuizOption) =>
                    checked &&
                    (picked.includes(o.id) || o.correct) &&
                    !!optionFeedback(o);
                // Only while feedback shows: without it the cards stay centred.
                const topAligned = q.options.some(
                    (o) => hasText(o.text) && showsFeedback(o)
                );
                return (
                <div key={q.id} className="flex flex-col gap-4">
                    <div>
                        <RichHtml html={seedTitleHtml(q.text, questionTag)} />
                        <QuizInstruction question={q} />
                    </div>

                    {open ? (
                        <textarea
                            value={openAnswers[q.id] ?? ""}
                            disabled={checked}
                            onChange={(e) =>
                                setOpenAnswers((a) => ({
                                    ...a,
                                    [q.id]: e.target.value,
                                }))
                            }
                            style={surfaceStyle}
                            className={openAnswerClass(appearance)}
                        />
                    ) : (
                    <div
                        className="grid sm:grid-cols-2"
                        style={{ gap: gridGap }}
                    >
                        {q.options
                            .filter((o) => hasText(o.text))
                            .map((o, oi) => {
                                const sel = picked.includes(o.id);
                                return (
                                    <button
                                        key={o.id}
                                        type="button"
                                        disabled={checked}
                                        onClick={() => toggle(q, o.id)}
                                        style={surfaceStyle}
                                        className={optionCardClass(appearance, {
                                            selected: sel,
                                            checked,
                                            correct: o.correct,
                                            topAligned,
                                        })}
                                    >
                                        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--lomda-muted))] lomda-v3-text-small text-[hsl(var(--lomda-primary))]">
                                            {LETTERS[oi] ?? oi + 1}
                                        </span>
                                        {type === "true-false" ? (
                                            <span className="lomda-v3-text-p min-w-0 flex-1">
                                                {trueFalseLabel(
                                                    q.options.indexOf(o),
                                                    locale
                                                )}
                                            </span>
                                        ) : (
                                            <span
                                                className="lomda-v3-text-p min-w-0 flex-1"
                                                dangerouslySetInnerHTML={{
                                                    __html: o.text,
                                                }}
                                            />
                                        )}
                                        <ChoiceControl
                                            variant={
                                                q.multiple
                                                    ? "checkbox"
                                                    : "radio"
                                            }
                                            status={
                                                checked && o.correct
                                                    ? "correct"
                                                    : checked && sel
                                                      ? "wrong"
                                                      : sel
                                                        ? "on"
                                                        : "off"
                                            }
                                        />
                                        {showsFeedback(o) && (
                                            <OptionFeedback option={o} />
                                        )}
                                    </button>
                                );
                            })}
                    </div>
                    )}

                    {/* The explanation must be noticed, so it reads as a small
                        heading + normal-colour body — never muted. */}
                    {checked && open && <OpenFeedback question={q} />}
                    {checked && !open && q.explanation && (
                        <div className="flex flex-col gap-1">
                            {/* Label matches the question's instruction line
                                ("Open question" etc.); the explanation itself is
                                normal paragraph text so it gets noticed. */}
                            <span className="lomda-v3-text-small text-[hsl(var(--lomda-muted-text))]">
                                {t("lomda-v3-editor.quiz.explanation", locale)}
                            </span>
                            <p className="lomda-v3-text-p text-[hsl(var(--lomda-text))]">
                                {q.explanation}
                            </p>
                        </div>
                    )}
                </div>
                );
            })}

            {checked ? (
                <div className="flex items-center justify-between">
                    {gradable.length > 0 ? (
                        <span className="lomda-v3-text-small">
                            {t("lomda-v3-editor.quiz.score", locale, {
                                correct: score,
                                total: gradable.length,
                            })}
                        </span>
                    ) : (
                        <span />
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setChecked(false);
                            setAnswers({});
                            setOpenAnswers({});
                        }}
                        className="lomda-v3-text-small flex items-center gap-1.5 rounded-full border px-4 py-2 text-[hsl(var(--lomda-muted-text))] transition hover:text-[hsl(var(--lomda-text))]"
                    >
                        <RotateCcw className="size-4" />
                        {t("lomda-v3-editor.quiz.retry", locale)}
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    disabled={!answered}
                    onClick={() => setChecked(true)}
                    className="self-center rounded-full bg-primary px-8 py-2.5 lomda-v3-text-label text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                >
                    {t("lomda-v3-editor.quiz.check", locale)}
                </button>
            )}
        </div>
    );
}

export default function KnowledgeCheckBlock({
    block,
}: {
    block: KnowledgeCheckBlockType;
}) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const setFocusedQuestion = useLomdaV3Editor((s) => s.setFocusedQuestion);
    const focusedQuestionId = useLomdaV3Editor((s) => s.focusedQuestionId);
    const clearActiveEditor = useLomdaV3Editor((s) => s.clearActiveEditor);
    const editable = useBlockEditable();
    const gridGap = useResolvedGridGap(block.id);
    const { questions } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveQuizAppearance(block.data.appearance, theme);

    // From the latest data, so a field committing as it unmounts never writes
    // back an older copy; a question or option that is gone writes nothing.
    const editQuestion = (id: string, edit: (q: QuizQuestion) => QuizQuestion) =>
        patchBlockData(block.id, "knowledge-check", (d) => {
            const q = d.questions.find((x) => x.id === id);
            const next = q && edit(q);
            return next && next !== q
                ? { questions: d.questions.map((x) => (x.id === id ? next : x)) }
                : null;
        });
    const patchQuestion = (id: string, patch: Partial<QuizQuestion>) =>
        editQuestion(id, (q) => ({ ...q, ...patch }));
    const patchOption = (
        qId: string,
        optId: string,
        patch: Partial<QuizOption>
    ) =>
        editQuestion(qId, (q) =>
            q.options.some((o) => o.id === optId)
                ? {
                      ...q,
                      options: q.options.map((o) =>
                          o.id === optId ? { ...o, ...patch } : o
                      ),
                  }
                : q
        );

    // Single-answer questions allow only one correct option (radio behaviour).
    const toggleCorrect = (qId: string, optId: string) =>
        editQuestion(qId, (q) => {
            const target = q.options.find((o) => o.id === optId);
            if (!target) return q;
            const single = quizType(q) !== "multiple";
            return {
                ...q,
                options: q.options.map((o) =>
                    o.id === optId
                        ? { ...o, correct: !target.correct }
                        : single
                          ? { ...o, correct: false }
                          : o
                ),
            };
        });
    const deleteQuestion = (id: string) =>
        patchBlockData(block.id, "knowledge-check", (d) => ({
            questions: d.questions.filter((q) => q.id !== id),
        }));

    if (!editable) {
        // An OPEN question is answered in a free-text box and has NO options, so
        // requiring one filtered it out of the preview entirely (and blanked the
        // whole block when every question was open). Only choice questions need
        // options.
        const valid = questions.filter(
            (q) =>
                hasText(q.text) &&
                (quizType(q) === "open" ||
                    q.options.some((o) => hasText(o.text)))
        );
        if (valid.length === 0) return null;
        return (
            <PreviewQuiz
                questions={valid}
                gridGap={gridGap}
                appearance={block.data.appearance}
            />
        );
    }

    // Add question / add option / type / correct answer / manage live on the
    // two-tier topbar (KnowledgeCheckSettings). Only a per-question delete
    // affordance stays inline (the topbar has no per-question delete).
    return (
        // Capture-phase clear runs before a question's own handler re-sets it —
        // so clicking a question focuses it, clicking the block empty area shows
        // only the block-level (1st-tier) settings.
        <div
            onPointerDownCapture={(e) => {
                setFocusedQuestion(null);
                // Clicking a non-text control drops the text editor so the
                // block/question settings return instead of the text toolbar.
                if (
                    !(e.target as HTMLElement).closest?.(".lomda-v3-rich-text")
                )
                    clearActiveEditor();
            }}
            className="flex flex-col gap-4 p-4"
        >
            {questions.map((q, qi) => (
                <div
                    key={q.id}
                    className={cn(
                        "group/q relative rounded-xl p-3 transition",
                        focusedQuestionId === q.id
                            ? "bg-[hsl(var(--lomda-hover))]/60"
                            : "hover:bg-[hsl(var(--lomda-hover))]/40"
                    )}
                >
                    <QuestionCard
                        q={q}
                        index={qi}
                        appearance={appearance}
                        blockId={block.id}
                        gridGap={gridGap}
                        onPatch={(patch) => patchQuestion(q.id, patch)}
                        onPatchOption={(optId, patch) =>
                            patchOption(q.id, optId, patch)
                        }
                        onToggleCorrect={(optId) => toggleCorrect(q.id, optId)}
                        onFocus={() => setFocusedQuestion(q.id)}
                    />
                    {questions.length > 1 && (
                        <button
                            type="button"
                            aria-label={t("lomda-v3-editor.quiz.delete-question")}
                            onClick={() => deleteQuestion(q.id)}
                            className="absolute end-0 top-0 flex size-7 items-center justify-center rounded text-muted-foreground opacity-0 transition hover:text-destructive group-hover/q:opacity-100"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}
